import { Box, Button } from '@mui/material';
import { SimpleFormLabel } from 'components/dialogs/SimpleFormDialog';
import { DescriptiveSelect, DescriptiveSelectOption } from 'components/uiElements/inputs/DescriptiveSelect';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDashboard } from 'routes/dashboard/Dashboard';
import { AutoWidgetQueryBehavior, WidgetQueryBehavior, WidgetTemplateQueryBehavior } from 'system/widget/Widget';

export enum MultiQueryRequestSettingsPresets {
  AUTO_SINGLE = 'AUTO_SINGLE',
  AUTO_MULTI = 'AUTO_MULTI',
  AUTO_MULTI_ALWAYS = 'AUTO_MULTI_ALWAYS'
}

export interface CustomMultiQueryRequestSettingsOptions {
  defaultAutoWidgetQueryBehavior: AutoWidgetQueryBehavior;
  autoWidgetQueryBehaviorOptions: AutoWidgetQueryBehavior[];

  defaultWidgetQueryBehavior: WidgetQueryBehavior;
  widgetQueryBehaviorOptions: WidgetQueryBehavior[];

  defaultWidgetTemplateQueryBehavior: WidgetTemplateQueryBehavior;
  widgetTemplateQueryBehaviorOptions: WidgetTemplateQueryBehavior[];
}

const autoSingleOptions = {
  defaultAutoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE,
  autoWidgetQueryBehaviorOptions: [
    AutoWidgetQueryBehavior.AUTO_SINGLE,
    AutoWidgetQueryBehavior.CUSTOM
  ],

  defaultWidgetQueryBehavior: WidgetQueryBehavior.SINGLE_INTERSECTION,
  widgetQueryBehaviorOptions: [WidgetQueryBehavior.SINGLE_INTERSECTION],

  defaultWidgetTemplateQueryBehavior: WidgetTemplateQueryBehavior.SINGLE_COMBINED,
  widgetTemplateQueryBehaviorOptions: [
    WidgetTemplateQueryBehavior.SINGLE_COMBINED,
    WidgetTemplateQueryBehavior.SINGLE_INTERSECTION,
    WidgetTemplateQueryBehavior.NONE
  ]
} as CustomMultiQueryRequestSettingsOptions;

const autoMultiOptions = {
  defaultAutoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_MULTI_WHEN_ALL,
  autoWidgetQueryBehaviorOptions: [
    AutoWidgetQueryBehavior.AUTO_MULTI_WHEN_ALL,
    AutoWidgetQueryBehavior.AUTO_MULTI_ALWAYS,
    AutoWidgetQueryBehavior.AUTO_SINGLE,
    AutoWidgetQueryBehavior.CUSTOM
  ],

  defaultWidgetQueryBehavior: WidgetQueryBehavior.MULTI_REQUEST,
  widgetQueryBehaviorOptions: [
    WidgetQueryBehavior.MULTI_REQUEST,
    WidgetQueryBehavior.SINGLE_INTERSECTION
  ],

  defaultWidgetTemplateQueryBehavior: WidgetTemplateQueryBehavior.SINGLE_COMBINED,
  widgetTemplateQueryBehaviorOptions: [
    WidgetTemplateQueryBehavior.MULTI_WHEN_ALL,
    WidgetTemplateQueryBehavior.MULTI_ALWAYS,
    WidgetTemplateQueryBehavior.SINGLE_COMBINED,
    WidgetTemplateQueryBehavior.SINGLE_INTERSECTION,
    WidgetTemplateQueryBehavior.NONE
  ]
} as CustomMultiQueryRequestSettingsOptions;

const autoMultiAlwaysOptions = {
  defaultAutoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_MULTI_ALWAYS,
  autoWidgetQueryBehaviorOptions: [
    AutoWidgetQueryBehavior.AUTO_MULTI_ALWAYS,
    AutoWidgetQueryBehavior.CUSTOM
  ],

  defaultWidgetQueryBehavior: WidgetQueryBehavior.MULTI_REQUEST,
  widgetQueryBehaviorOptions: [
    WidgetQueryBehavior.MULTI_REQUEST,
    WidgetQueryBehavior.SINGLE_INTERSECTION
  ],

  defaultWidgetTemplateQueryBehavior: WidgetTemplateQueryBehavior.SINGLE_COMBINED,
  widgetTemplateQueryBehaviorOptions: [
    WidgetTemplateQueryBehavior.MULTI_ALWAYS,
    WidgetTemplateQueryBehavior.SINGLE_COMBINED,
    WidgetTemplateQueryBehavior.SINGLE_INTERSECTION,
    WidgetTemplateQueryBehavior.NONE
  ]
} as CustomMultiQueryRequestSettingsOptions;

export interface RequestSettingsOptions extends CustomMultiQueryRequestSettingsOptions {
  activeAutoWidgetQueryBehaviorOptions: DescriptiveSelectOption[];
  activeWidgetQueryBehaviorOptions: DescriptiveSelectOption[];
  activeWidgetTemplateQueryBehaviorOptions: DescriptiveSelectOption[];
}

function getPresetOptions(presetType: MultiQueryRequestSettingsPresets): CustomMultiQueryRequestSettingsOptions | undefined {
  switch (presetType) {
    case MultiQueryRequestSettingsPresets.AUTO_SINGLE:
      return autoSingleOptions;
    case MultiQueryRequestSettingsPresets.AUTO_MULTI:
      return autoMultiOptions;
    case MultiQueryRequestSettingsPresets.AUTO_MULTI_ALWAYS:
      return autoMultiAlwaysOptions;
  }
}

export interface MultiQueryRequestSettingsProps {
  hideAdvancedToggle?: boolean;

  autoWidgetQueryBehavior?: AutoWidgetQueryBehavior;
  setAutoWidgetQueryBehavior: (behavior: AutoWidgetQueryBehavior) => void;

  widgetQueryBehavior: WidgetQueryBehavior;
  setWidgetQueryBehavior: (behavior: WidgetQueryBehavior) => void;

  widgetTemplateQueryBehavior: WidgetTemplateQueryBehavior;
  setWidgetTemplateQueryBehavior: (behavior: WidgetTemplateQueryBehavior) => void;

  // Quick Options, most widgets should use these.
  // If this is not provided, customOptions must be provided.
  presetOptionsType?: MultiQueryRequestSettingsPresets;

  // Fully Custom Options - these are ignored if presetOptionsType is provided.
  // Few widgets will need to use this.
  customOptions?: CustomMultiQueryRequestSettingsOptions;
}

export default function MultiQueryRequestSettings(props: MultiQueryRequestSettingsProps): JSX.Element {
  const { i18n } = useTranslation();
  const dashboard = useDashboard();

  const {
    hideAdvancedToggle,
    presetOptionsType,
    autoWidgetQueryBehavior, setAutoWidgetQueryBehavior,
    widgetQueryBehavior, setWidgetQueryBehavior,
    widgetTemplateQueryBehavior, setWidgetTemplateQueryBehavior
  } = props;

  const presetOptions = useMemo<RequestSettingsOptions>(() => {
    let options = props.customOptions as RequestSettingsOptions | undefined;
    if (presetOptionsType) {
      options = getPresetOptions(presetOptionsType) as RequestSettingsOptions | undefined;
    }
    if (!options) {
      return {
        activeAutoWidgetQueryBehaviorOptions: [],
        activeWidgetQueryBehaviorOptions: [],
        activeWidgetTemplateQueryBehaviorOptions: [],
        defaultAutoWidgetQueryBehavior: AutoWidgetQueryBehavior.CUSTOM,
        autoWidgetQueryBehaviorOptions: [],
        defaultWidgetQueryBehavior: WidgetQueryBehavior.SINGLE_INTERSECTION,
        widgetQueryBehaviorOptions: [],
        defaultWidgetTemplateQueryBehavior: WidgetTemplateQueryBehavior.SINGLE_COMBINED,
        widgetTemplateQueryBehaviorOptions: []
      } as RequestSettingsOptions;
    }

    options.activeAutoWidgetQueryBehaviorOptions = options.autoWidgetQueryBehaviorOptions.map((option) => {
      return {
        label: i18n.t(`widget-settings.auto-widget-query-behavior.${option}`),
        description: i18n.t(`widget-settings.auto-widget-query-behavior-descriptions.${option}`),
        value: option
      };
    });

    options.activeWidgetTemplateQueryBehaviorOptions = options.widgetTemplateQueryBehaviorOptions.map((option) => {
      return {
        label: i18n.t(`widget-settings.widget-template-query-behavior.${option}`),
        description: i18n.t(`widget-settings.widget-template-query-behavior-descriptions.${option}`),
        value: option
      };
    });

    options.activeWidgetQueryBehaviorOptions = options.widgetQueryBehaviorOptions.map((option) => {
      return {
        label: i18n.t(`widget-settings.widget-query-behavior.${option}`),
        description: i18n.t(`widget-settings.widget-query-behavior-descriptions.${option}`),
        value: option
      };
    });

    // The user cannot select Multi options for both widget and template query behaviors
    // We prioritize the template behavior if both are set to multi
    if (widgetTemplateQueryBehavior && widgetTemplateQueryBehavior === WidgetTemplateQueryBehavior.MULTI_ALWAYS || widgetTemplateQueryBehavior === WidgetTemplateQueryBehavior.MULTI_WHEN_ALL) {
      options.activeWidgetQueryBehaviorOptions = options.activeWidgetQueryBehaviorOptions.filter((option) => {
        return option.value !== WidgetQueryBehavior.MULTI_REQUEST;
      });
    }

    return options;
  }, [presetOptionsType, props.customOptions, i18n]);

  // Enforce valid selections based on available options
  useEffect(() => {
    if (!presetOptions) {
      return;
    }

    const autoBehaviorOptionsValues = presetOptions.activeAutoWidgetQueryBehaviorOptions.map((option) => option.value);
    if (autoWidgetQueryBehavior && !autoBehaviorOptionsValues.includes(autoWidgetQueryBehavior)) {
      setAutoWidgetQueryBehavior(presetOptions.defaultAutoWidgetQueryBehavior);
    }

    const templateBehaviorOptionsValues = presetOptions.activeWidgetTemplateQueryBehaviorOptions.map((option) => option.value);
    if (widgetTemplateQueryBehavior && !templateBehaviorOptionsValues.includes(widgetTemplateQueryBehavior)) {
      setWidgetTemplateQueryBehavior(presetOptions.defaultWidgetTemplateQueryBehavior);
    }

    const widgetBehaviorOptionsValues = presetOptions.activeWidgetQueryBehaviorOptions.map((option) => option.value);
    if (widgetQueryBehavior === WidgetQueryBehavior.MULTI_REQUEST && widgetTemplateQueryBehavior && (widgetTemplateQueryBehavior === WidgetTemplateQueryBehavior.MULTI_ALWAYS || widgetTemplateQueryBehavior === WidgetTemplateQueryBehavior.MULTI_WHEN_ALL)) {
      // If template behavior is multi, widget query behavior cannot be multi
      setWidgetQueryBehavior(WidgetQueryBehavior.SINGLE_INTERSECTION);
    } else if (widgetQueryBehavior && !widgetBehaviorOptionsValues.includes(widgetQueryBehavior)) {
      setWidgetQueryBehavior(presetOptions.defaultWidgetQueryBehavior);
    }

  }, [autoWidgetQueryBehavior, widgetQueryBehavior, widgetTemplateQueryBehavior, presetOptions, setWidgetQueryBehavior, setWidgetTemplateQueryBehavior]);

  const [advancedOptionsOpen, setAdvancedOptionsOpen] = useState<boolean>(false);
  const showAdvancedOptions = useMemo(() => {
    if (hideAdvancedToggle || advancedOptionsOpen) {
      return true;
    }
    if (!autoWidgetQueryBehavior || autoWidgetQueryBehavior === AutoWidgetQueryBehavior.CUSTOM) {
      return true;
    }
    return false;
  }, [hideAdvancedToggle, advancedOptionsOpen, autoWidgetQueryBehavior]);

  const showCustomOptions = useMemo(() => {
    return !autoWidgetQueryBehavior || autoWidgetQueryBehavior === AutoWidgetQueryBehavior.CUSTOM;
  }, [autoWidgetQueryBehavior]);

  return (
    <>
      {!hideAdvancedToggle &&
        <Button
          variant="text"
          sx={{mt:2}}
          onClick={() => {
            setAdvancedOptionsOpen(!advancedOptionsOpen);
          }} >
          {advancedOptionsOpen ? i18n.t("dashboard.hide-advanced-options") : i18n.t("dashboard.show-advanced-options")}
        </Button>
      }
      {showAdvancedOptions &&
        <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          {presetOptions && presetOptions.activeAutoWidgetQueryBehaviorOptions.length > 1 && (
            <Box>
              <SimpleFormLabel>{i18n.t("widget-settings.auto-widget-query-behavior-title")}</SimpleFormLabel>
              <DescriptiveSelect
                value={autoWidgetQueryBehavior || presetOptions.defaultAutoWidgetQueryBehavior}
                onChange={(value) => {
                  setAdvancedOptionsOpen(true);
                  setAutoWidgetQueryBehavior(value as AutoWidgetQueryBehavior);
                }}
                options={presetOptions.activeAutoWidgetQueryBehaviorOptions} />
            </Box>
          )}
          {showCustomOptions && (<>
            {presetOptions && presetOptions.activeWidgetTemplateQueryBehaviorOptions.length > 1 && (
              <Box>
                <SimpleFormLabel>{i18n.t("widget-settings.widget-template-query-behavior-title")}</SimpleFormLabel>
                <DescriptiveSelect
                  value={widgetTemplateQueryBehavior || WidgetTemplateQueryBehavior.NONE}
                  onChange={(value) => {
                    setWidgetTemplateQueryBehavior(value as WidgetTemplateQueryBehavior);
                  }}
                  options={presetOptions.activeWidgetTemplateQueryBehaviorOptions} />
              </Box>
            )}
            {presetOptions && presetOptions.activeWidgetQueryBehaviorOptions.length > 1 && (
              <Box>
                <SimpleFormLabel>{i18n.t("widget-settings.widget-query-behavior-title")}</SimpleFormLabel>
                <DescriptiveSelect
                  value={widgetQueryBehavior || WidgetQueryBehavior.SINGLE_INTERSECTION}
                  onChange={(value) => {
                    setWidgetQueryBehavior(value as WidgetQueryBehavior);
                  }}
                  options={presetOptions.activeWidgetQueryBehaviorOptions} />
              </Box>
            )}
          </>)}
        </Box>
      }
    </>
  );
}